import type { AdminSession, CustomPage, InquiryInput } from './types';

interface ErrorPayload {
  error?: string;
  code?: string;
}

export class ApiError extends Error {
  constructor(message: string, readonly code: string, readonly status: number) {
    super(message);
    this.name = 'ApiError';
  }
}

async function readJson<ResponseBody>(path: string): Promise<ResponseBody> {
  const response = await fetch(path, { headers: { accept: 'application/json' } });

  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as ErrorPayload;
    throw new ApiError(body.error ?? 'Request failed', body.code ?? 'REQUEST_FAILED', response.status);
  }

  return response.json() as Promise<ResponseBody>;
}

export async function getPublicPage(slug: string) {
  try {
    const body = await readJson<{ page: CustomPage }>(`/api/public/pages/${encodeURIComponent(slug)}`);
    return body.page;
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

export async function getPublicNavigation() {
  const body = await readJson<{ pages: CustomPage[] }>('/api/public/navigation');
  return body.pages;
}

export async function getPublicPosts() {
  return (await readJson<{ posts: CustomPage[] }>('/api/public/posts')).posts;
}

export async function getPublicPost(slug: string) {
  try {
    return (await readJson<{ page: CustomPage }>(`/api/public/posts/${encodeURIComponent(slug)}`)).page;
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

export async function submitInquiry(inquiry: InquiryInput) {
  const response = await fetch('/api/public/inquiries', {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify(inquiry)
  });
  const body = await response.json().catch(() => ({})) as ErrorPayload & { inquiryId?: string };
  if (!response.ok) throw new ApiError(body.error ?? 'Unable to send your inquiry.', body.code ?? 'REQUEST_FAILED', response.status);
  return body.inquiryId ?? '';
}

export async function getAdminSession() {
  return readJson<AdminSession>('/api/admin/me');
}

export async function createAdminSession() {
  const response = await fetch('/api/auth/session', {
    method: 'POST',
    headers: { accept: 'application/json' }
  });
  const body = await response.json().catch(() => ({})) as ErrorPayload & Partial<AdminSession>;
  if (!response.ok) throw new ApiError(body.error ?? 'Unable to open the admin.', body.code ?? 'SESSION_CREATE_FAILED', response.status);
  return body as AdminSession;
}

export async function adminLogout(csrfToken: string) {
  await fetch('/api/auth/logout', { method: 'POST', headers: { accept: 'application/json', 'x-csrf-token': csrfToken } });
}

async function adminWrite<ResponseBody>(path: string, method: 'POST' | 'PUT' | 'DELETE', csrfToken: string, body?: unknown) {
  const response = await fetch(path, {
    method,
    headers: { accept: 'application/json', 'content-type': 'application/json', 'x-csrf-token': csrfToken },
    body: body === undefined ? undefined : JSON.stringify(body)
  });
  const payload = await response.json().catch(() => ({})) as ErrorPayload & ResponseBody;
  if (!response.ok) throw new ApiError(payload.error ?? 'Unable to save changes.', payload.code ?? 'REQUEST_FAILED', response.status);
  return payload;
}

export async function getAdminInquiries() {
  const body = await readJson<{ inquiries: import('./types').AdminInquiry[] }>('/api/admin/inquiries');
  return body.inquiries;
}

export interface AdminDashboardCounts {
  new_inquiries: number;
  published_posts: number;
  published_items: number;
}

export async function getAdminDashboardCounts() {
  const body = await readJson<{ counts: AdminDashboardCounts }>('/api/admin/dashboard');
  return body.counts;
}

export function updateAdminInquiry(id: string, status: import('./types').InquiryStatus, note: string, csrfToken: string) {
  return adminWrite<{ id: string; status: string }>(`/api/admin/inquiries/${encodeURIComponent(id)}`, 'PUT', csrfToken, { status, note });
}

export async function getAdminPages(kind: 'page' | 'post' = 'page') { return (await readJson<{ pages: import('./types').CustomPage[] }>(`/api/admin/pages?kind=${kind}`)).pages; }
export async function getPublicCatalog() { return (await readJson<{ items: import('./types').CatalogItem[] }>('/api/public/catalog')).items; }
export async function getPublicCatalogItem(slug: string) {
  try {
    return (await readJson<{ item: import('./types').CatalogItem }>(`/api/public/catalog/${encodeURIComponent(slug)}`)).item;
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}
export async function listAdminCatalog() { return (await readJson<{ items: import('./types').AdminCatalogItem[] }>('/api/admin/catalog')).items; }
export function saveCatalogItem(csrfToken: string, input: import('./types').CatalogItemInput, id?: string) { return adminWrite<{ id: string }>(id ? `/api/admin/catalog/${encodeURIComponent(id)}` : '/api/admin/catalog', id ? 'PUT' : 'POST', csrfToken, input); }
export function archiveCatalogItem(csrfToken: string, id: string) { return adminWrite<{ archived: boolean }>(`/api/admin/catalog/${encodeURIComponent(id)}`, 'DELETE', csrfToken); }
export function saveAdminPage(page: import('./types').CustomPageInput, csrfToken: string, id?: string) { return adminWrite<{ id: string }>(id ? `/api/admin/pages/${encodeURIComponent(id)}` : '/api/admin/pages', id ? 'PUT' : 'POST', csrfToken, page); }
export function archiveAdminPage(id: string, csrfToken: string) { return adminWrite<{ archived: boolean }>(`/api/admin/pages/${encodeURIComponent(id)}`, 'DELETE', csrfToken); }
export async function getAdminMedia() { return (await readJson<{ media: import('./types').MediaItem[] }>('/api/admin/media')).media; }
export async function uploadAdminMedia(form: FormData, csrfToken: string) { const response = await fetch('/api/admin/media', { method: 'POST', headers: { accept: 'application/json', 'x-csrf-token': csrfToken }, body: form }); const payload = await response.json().catch(() => ({})) as ErrorPayload & { id?: string }; if (!response.ok) throw new ApiError(payload.error ?? 'Unable to upload file.', payload.code ?? 'UPLOAD_FAILED', response.status); return payload; }
export function updateAdminMedia(id: string, value: Pick<import('./types').MediaItem, 'label' | 'altText' | 'status' | 'sortOrder'>, csrfToken: string) { return adminWrite<{ id: string }>(`/api/admin/media/${encodeURIComponent(id)}`, 'PUT', csrfToken, value); }
export function archiveAdminMedia(id: string, csrfToken: string) { return adminWrite<{ archived: boolean }>(`/api/admin/media/${encodeURIComponent(id)}`, 'DELETE', csrfToken); }
